import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', pin: '', role: 'seller' });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Erro ao carregar usuários');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.pin.length !== 4 || !/^\d+$/.test(formData.pin)) {
      alert('PIN deve ter exatamente 4 dígitos');
      return;
    }

    try {
      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, formData);
      } else {
        await axios.post(`${API}/users`, formData);
      }
      setIsDialogOpen(false);
      setEditingUser(null);
      setFormData({ name: '', pin: '', role: 'seller' });
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao salvar usuário');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Deseja realmente excluir este usuário?')) {
      try {
        await axios.delete(`${API}/users/${id}`);
        fetchUsers();
      } catch (error) {
        alert('Erro ao excluir usuário');
      }
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({ name: user.name, pin: user.pin, role: user.role });
    setIsDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormData({ name: '', pin: '', role: 'seller' });
    setIsDialogOpen(true);
  };

  return (
    <AdminLayout title="Usuários">
      <div className="flex justify-between items-center mb-6">
        <p className="text-white/60 font-inter">Gerencie os usuários do sistema</p>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="create-user-button"
              onClick={openCreateDialog}
              className="bg-[#FF5500] hover:bg-[#CC4400] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Usuário
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#18181b] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-oswald text-2xl">
                {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-white/80">Nome</Label>
                <Input
                  id="name"
                  data-testid="user-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-[#27272a] border-white/10 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="pin" className="text-white/80">PIN (4 dígitos)</Label>
                <Input
                  id="pin"
                  data-testid="user-pin-input"
                  type="text"
                  maxLength={4}
                  value={formData.pin}
                  onChange={(e) => setFormData({ ...formData, pin: e.target.value.replace(/\D/g, '') })}
                  className="bg-[#27272a] border-white/10 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="role" className="text-white/80">Tipo</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                  required
                >
                  <SelectTrigger data-testid="user-role-select" className="bg-[#27272a] border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#27272a] border-white/10 text-white">
                    <SelectItem value="seller">Vendedor</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                data-testid="submit-user-button"
                type="submit"
                className="w-full bg-[#FF5500] hover:bg-[#CC4400] text-white"
              >
                {editingUser ? 'Atualizar' : 'Criar'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-[#18181b] border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#27272a] border-b border-white/10">
            <tr>
              <th className="px-6 py-4 text-left font-oswald text-white">Nome</th>
              <th className="px-6 py-4 text-left font-oswald text-white">PIN</th>
              <th className="px-6 py-4 text-left font-oswald text-white">Tipo</th>
              <th className="px-6 py-4 text-right font-oswald text-white">Ações</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr
                key={user.id}
                data-testid={`user-row-${user.name}`}
                className="border-b border-white/5 hover:bg-white/5 transition-all"
              >
                <td className="px-6 py-4 font-inter text-white">{user.name}</td>
                <td className="px-6 py-4 font-oswald text-white/60">***{user.pin.slice(-1)}</td>
                <td className="px-6 py-4 font-inter">
                  <span
                    className={`px-3 py-1 rounded-full text-xs ${
                      user.role === 'admin'
                        ? 'bg-[#FF5500]/20 text-[#FF5500]'
                        : 'bg-white/10 text-white/70'
                    }`}
                  >
                    {user.role === 'admin' ? 'Admin' : 'Vendedor'}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      data-testid={`edit-user-${user.name}`}
                      onClick={() => openEditDialog(user)}
                      className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"
                    >
                      <Edit className="w-4 h-4 text-white" />
                    </button>
                    <button
                      data-testid={`delete-user-${user.name}`}
                      onClick={() => handleDelete(user.id)}
                      className="w-8 h-8 rounded-lg bg-red-500/20 hover:bg-red-500/30 flex items-center justify-center transition-all"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {users.length === 0 && (
        <div className="text-center py-12 text-white/40 font-inter">
          Nenhum usuário cadastrado.
        </div>
      )}
    </AdminLayout>
  );
};

export default Users;